import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { FaImage, FaTimesCircle, FaSpinner } from 'react-icons/fa';
import api from '../services/apiService';
import imageCompression from 'browser-image-compression';
import heic2any from 'heic2any';
import './ImageUploader.scss';

const ImageUploader = ({ onUploadSuccess, matchId, teamId }) => {
  const [previews, setPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const onDrop = useCallback(async (acceptedFiles) => {
    if (!matchId) {
      setUploadError('Match ID is not specified. Cannot upload image.');
      return;
    }

    setUploading(true);
    setUploadError(null);

    for (let file of acceptedFiles) {
      try {
        if (file.type === 'image/heic' || file.name.toLowerCase().endsWith('.heic')) {
          const convertedBlob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.8 });
          file = new File([convertedBlob], `${file.name.split('.')[0]}.jpeg`, { type: 'image/jpeg' });
        }
        
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1024,
          useWebWorker: true,
          initialQuality: 0.8
        };
        const compressedFile = await imageCompression(file, options);

        const newPreview = Object.assign(compressedFile, {
          preview: URL.createObjectURL(compressedFile)
        });
        setPreviews(prev => [...prev, newPreview]);
        
        const formData = new FormData();
        formData.append('image', compressedFile, compressedFile.name);
        formData.append('match_id', matchId);
        formData.append('team_id', teamId);

        const response = await api.post('/upload_image.php', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (response.data.success) {
          onUploadSuccess(response.data.url);
        } else {
          setUploadError(response.data.message || 'An unknown error occurred.');
        }

      } catch (error) {
        setUploadError('Failed to process or upload image.');
        console.error(error);
      }
    }

    setUploading(false);
  }, [matchId, teamId, onUploadSuccess]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
      'image/webp': ['.webp'],
      'image/heic': ['.heic']
    },
    multiple: true
  });

  const removePreview = (fileToRemove) => {
    setPreviews(prev => prev.filter(file => file !== fileToRemove));
    URL.revokeObjectURL(fileToRemove.preview);
  };

  return (
    <div className="image-uploader">
      <div {...getRootProps()} className={`dropzone ${isDragActive ? 'active' : ''}`}>
        <input {...getInputProps()} />
        <FaImage className="dropzone-icon" />
        <p>Drag & drop images here, or click to select files</p>
      </div>

      {uploading && (
        <div className="upload-status">
          <FaSpinner className="spinner" />
          <span>Uploading...</span>
        </div>
      )}

      {uploadError && (
        <div className="upload-error">
          <p>{uploadError}</p>
        </div>
      )}

      <div className="preview-area">
        {previews.map((file, index) => (
          <div key={index} className="preview-item">
            <img src={file.preview} alt={`Preview ${index}`} />
            <button onClick={() => removePreview(file)} className="remove-btn">
              <FaTimesCircle />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ImageUploader; 